import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  Film,
  HelpCircle,
  Play,
  Signal,
  Smartphone,
  Square,
  Trash2,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { useBrand } from "../../context/BrandContext";
import { useChampionshipClipUploads } from "../../hooks/useChampionshipClipUploads";
import {
  MATCH_UPLOAD_MODES,
  estimateClipStorage,
  groupLocalMatchClips,
  listLocalMatchClipMeta,
  readLocalMatchClip,
  readMatchUploadMode,
  writeMatchUploadMode,
} from "../../utils/championshipLocalClips";
import {
  cancelChampionshipClipUpload,
  clipUploadErrorText,
  clipUploadStatusText,
  deleteChampionshipClips,
  flushChampionshipClips,
  kickChampionshipClipUploads,
  onChampionshipClipUploaded,
} from "../../utils/championshipClipUploader";
import { getNetworkTransport } from "../../utils/videoUploadNetwork";
import "./ChampionshipPhoneVideosPage.css";

const MB = 1024 * 1024;
const GB = 1024 * MB;

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n >= GB) return `${(n / GB).toFixed(1)} GB`;
  if (n >= 10 * MB) return `${Math.round(n / MB)} MB`;
  return `${Math.max(0.1, n / MB).toFixed(1)} MB`;
}

function minutesOf(ms) {
  return Math.max(1, Math.round((Number(ms) || 0) / 60_000));
}

function angleLabel(value, t) {
  const raw = String(value || "").toLowerCase().trim();
  if (raw === "scorecam_video") {
    return t("cmCamPositionScorecamVideo", "Scorecam (players)");
  }
  if (raw === "referee" || raw === "side" || raw === "long") {
    return t("cmCamPositionReferee", "Referee");
  }
  if (raw === "scorecam" || raw === "score" || raw === "score_camera") {
    return t("cmCamPositionScorecam", "Scorecam");
  }
  return t("cmCamPositionSpectator", "Spectator");
}

function useNetworkTransport() {
  const [transport, setTransport] = useState(getNetworkTransport);
  useEffect(() => {
    const update = () => setTransport(getNetworkTransport());
    const connection = navigator.connection;
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    connection?.addEventListener?.("change", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
      connection?.removeEventListener?.("change", update);
    };
  }, []);
  return transport;
}

const NETWORK_ICONS = { wifi: Wifi, cellular: Signal, none: WifiOff, unknown: HelpCircle };
const MODE_ICONS = { live: CloudUpload, wifi: Wifi, phone: Smartphone };

function networkLabel(transport, t) {
  switch (transport) {
    case "wifi":
      return t("cmPhoneVideosNetWifi", "On Wi‑Fi");
    case "cellular":
      return t("cmPhoneVideosNetCellular", "On mobile data");
    case "none":
      return t("cmPhoneVideosNetOffline", "No network");
    default:
      return t("cmPhoneVideosNetUnknown", "Network type unknown");
  }
}

function modeLabel(mode, t) {
  if (mode === "wifi") return t("cmWifiOnlyUpload", "Wi‑Fi only");
  if (mode === "phone") return t("cmUploadModePhone", "Save on phone");
  return t("cmUploadModeLive", "Live");
}

function modeNote(mode, t) {
  if (mode === "wifi") {
    return t(
      "cmWifiOnlyUploadOn",
      "On mobile data, clips stay on this phone and upload when Wi‑Fi is on."
    );
  }
  if (mode === "phone") {
    return t(
      "cmUploadModePhoneNote",
      "Every clip stays on this phone while it films. Uploads start on Wi‑Fi once filming stops, or from Videos on this phone."
    );
  }
  return t(
    "cmWifiOnlyUploadOff",
    "Clips upload as soon as each one finishes recording."
  );
}

function clipState(clip, uploads) {
  if (clip.uploaded) return "done";
  if (uploads.currentId === clip.id) return "active";
  if (uploads.errors?.[clip.id]) return "failed";
  return "waiting";
}

export default function ChampionshipPhoneVideosPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { themeMode } = useBrand();
  const mode = themeMode === "light" ? "light" : "dark";
  const uploads = useChampionshipClipUploads();
  const transport = useNetworkTransport();
  const [uploadMode, setUploadMode] = useState(readMatchUploadMode);
  const [clips, setClips] = useState(null);
  const [uploadedIds, setUploadedIds] = useState(() => new Set());
  const [storage, setStorage] = useState(null);
  const [player, setPlayer] = useState(null);
  const seenRef = useRef(new Map());
  const deletedRef = useRef(new Set());

  const reload = useCallback(async () => {
    let rows = [];
    try {
      rows = await listLocalMatchClipMeta();
    } catch {
      rows = [];
    }
    const present = new Set(rows.map((row) => row.id));
    const gone = [];
    seenRef.current.forEach((_, id) => {
      if (!present.has(id) && !deletedRef.current.has(id)) gone.push(id);
    });
    rows.forEach((row) => seenRef.current.set(row.id, row));
    if (gone.length) {
      setUploadedIds((prev) => {
        const next = new Set(prev);
        gone.forEach((id) => next.add(id));
        return next;
      });
    }
    setClips(rows);
    setStorage(await estimateClipStorage());
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, uploads.count, uploads.busy]);

  useEffect(() => onChampionshipClipUploaded(() => void reload()), [reload]);

  const groups = useMemo(() => {
    if (!clips) return null;
    const list = clips.map((clip) => ({ ...clip, uploaded: false }));
    uploadedIds.forEach((id) => {
      const meta = seenRef.current.get(id);
      if (meta) list.push({ ...meta, uploaded: true });
    });
    return groupLocalMatchClips(list);
  }, [clips, uploadedIds]);

  const matches = useMemo(() => {
    if (!groups) return [];
    const byMatch = new Map();
    for (const group of groups) {
      let entry = byMatch.get(group.matchId);
      if (!entry) {
        entry = { matchId: group.matchId, label: "", date: null, recordings: [] };
        byMatch.set(group.matchId, entry);
      }
      entry.recordings.push(group);
      if (!entry.label && group.info.matchLabel) entry.label = group.info.matchLabel;
      if (!entry.date && group.info.matchDate) entry.date = group.info.matchDate;
    }
    return [...byMatch.values()];
  }, [groups]);

  const waitingCount = clips?.length || 0;
  const waitingBytes = useMemo(
    () =>
      (clips || []).reduce(
        (sum, clip) => sum + (clip.byteSize || 0) + (clip.audioBytes || 0),
        0
      ),
    [clips]
  );

  const goBack = () => {
    if (location.key && location.key !== "default") navigate(-1);
    else navigate("/competition");
  };

  const applyMode = (next) => {
    setUploadMode(writeMatchUploadMode(next));
    kickChampionshipClipUploads();
  };

  const uploadIds = (ids) => {
    if (!ids.length) return;
    void flushChampionshipClips({ ids });
  };

  const deleteGroup = async (group) => {
    const waiting = group.clips.filter((clip) => !clip.uploaded);
    if (!waiting.length) return;
    const ids = waiting.map((clip) => clip.id);
    const waitingMs = waiting.reduce(
      (sum, clip) => sum + (Number(clip.info?.durationMs) || 60_000),
      0
    );
    const ok = window.confirm(
      t(
        "cmPhoneVideosDeleteConfirm",
        "Delete {{min}} min of video from this phone? It has not been uploaded and cannot be recovered.",
        { min: minutesOf(waitingMs) }
      )
    );
    if (!ok) return;
    ids.forEach((id) => deletedRef.current.add(id));
    await deleteChampionshipClips(ids);
    await reload();
  };

  const dateLocale = i18n.language || undefined;
  const formatWhen = (ms) => {
    try {
      return new Date(ms).toLocaleString(dateLocale, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };
  const formatMatchDate = (raw) => {
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" });
  };

  const NetIcon = NETWORK_ICONS[transport] || HelpCircle;
  const statusText = clipUploadStatusText(uploads, t);
  const overallPct =
    uploads.busy && uploads.total > 0
      ? Math.round(((uploads.done + (uploads.pct || 0) / 100) / uploads.total) * 100)
      : null;

  return (
    <main className={`cpv-page cpv-page--${mode}`}>
      <div className="cpv-page__inner">
        <header className="cpv-head">
          <button
            type="button"
            className="cpv-icon-btn"
            onClick={goBack}
            aria-label={t("back", "Back")}
          >
            <ArrowLeft size={20} aria-hidden />
          </button>
          <div className="cpv-head__text">
            <h1 className="cpv-head__title">
              {t("cmPhoneVideosTitle", "Videos on this phone")}
            </h1>
            <p className="cpv-head__meta" hidden={clips === null}>
              {waitingCount > 0
                ? t("cmPhoneVideosWaiting", "{{n}} clips · {{size}} to upload", {
                    n: waitingCount,
                    size: formatBytes(waitingBytes),
                  })
                : t("cmPhoneVideosNothingWaiting", "Nothing waiting to upload")}
              {storage?.free != null ? (
                <>
                  {" · "}
                  {t("cmPhoneVideosFree", "{{size}} free", {
                    size: formatBytes(storage.free),
                  })}
                </>
              ) : null}
            </p>
          </div>
        </header>

        <section className="cpv-sync" aria-label={t("cmPhoneVideosUploadSection", "Upload")}>
          <div className="cpv-sync__top">
            <span className={`cpv-net cpv-net--${transport}`}>
              <NetIcon size={14} aria-hidden />
              {networkLabel(transport, t)}
            </span>
            {uploads.busy ? (
              <button
                type="button"
                className="cpv-btn cpv-btn--ghost"
                onClick={cancelChampionshipClipUpload}
              >
                <Square size={14} aria-hidden />
                {t("cmPhoneVideosStop", "Stop")}
              </button>
            ) : (
              <button
                type="button"
                className="cpv-btn cpv-btn--primary"
                disabled={!waitingCount || transport === "none"}
                onClick={() => void flushChampionshipClips({ force: true })}
              >
                <CloudUpload size={16} aria-hidden />
                {t("cmPhoneVideosUploadAll", "Upload all")}
              </button>
            )}
          </div>
          {overallPct != null ? (
            <div
              className="cpv-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={overallPct}
            >
              <span style={{ width: `${overallPct}%` }} />
            </div>
          ) : null}
          {statusText ? (
            <p className="cpv-sync__status" aria-live="polite">
              {statusText}
            </p>
          ) : null}
          {transport === "unknown" && uploadMode !== "live" ? (
            <p className="cpv-note">
              {t(
                "cmUploadNetworkUnknown",
                "This phone does not tell the browser whether it is on Wi‑Fi. On mobile data, use Save on phone and tap Upload now once you are on Wi‑Fi."
              )}
            </p>
          ) : null}
          <div className="cpv-modes" role="radiogroup" aria-label={t("cmUploadModeLabel", "When to upload")}>
            {MATCH_UPLOAD_MODES.map((m) => {
              const Icon = MODE_ICONS[m];
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={uploadMode === m}
                  className={`cpv-mode${uploadMode === m ? " is-on" : ""}`}
                  onClick={() => applyMode(m)}
                >
                  <Icon size={15} aria-hidden />
                  {modeLabel(m, t)}
                </button>
              );
            })}
          </div>
          <p className="cpv-note">{modeNote(uploadMode, t)}</p>
        </section>

        {groups === null ? (
          <p className="cpv-empty__text">{t("loading", "Loading…")}</p>
        ) : matches.length === 0 ? (
          <div className="cpv-empty">
            <Film size={28} aria-hidden />
            <p className="cpv-empty__title">
              {t("cmPhoneVideosEmptyTitle", "No match videos on this phone")}
            </p>
            <p className="cpv-empty__text">
              {t(
                "cmPhoneVideosEmptyText",
                "Videos you film with Save on phone, or that could not upload, wait here until they are sent."
              )}
            </p>
          </div>
        ) : (
          matches.map((match) => (
            <section key={match.matchId} className="cpv-match">
              <div className="cpv-match__head">
                <div className="cpv-match__text">
                  <h2 className="cpv-match__title">
                    {match.label || t("cmPhoneVideosMatch", "Match")}
                  </h2>
                  {match.date ? (
                    <p className="cpv-match__date">{formatMatchDate(match.date)}</p>
                  ) : null}
                </div>
                {match.matchId ? (
                  <Link className="cpv-link" to={`/competition/match/${match.matchId}`}>
                    {t("cmPhoneVideosOpenMatch", "Open match")}
                  </Link>
                ) : null}
              </div>
              <ul className="cpv-recs">
                {match.recordings.map((rec) => (
                  <RecordingCard
                    key={rec.key}
                    rec={rec}
                    uploads={uploads}
                    transport={transport}
                    when={formatWhen(rec.startedAt)}
                    t={t}
                    onPlay={(index) => setPlayer({ key: rec.key, index })}
                    onUpload={uploadIds}
                    onDelete={() => void deleteGroup(rec)}
                  />
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
      {player && groups ? (
        <PhoneClipPlayer
          group={groups.find((g) => g.key === player.key)}
          startIndex={player.index}
          t={t}
          onClose={() => setPlayer(null)}
        />
      ) : null}
    </main>
  );
}

function RecordingCard({ rec, uploads, transport, when, t, onPlay, onUpload, onDelete }) {
  const total = rec.clips.length;
  const states = rec.clips.map((clip) => clipState(clip, uploads));
  const waitingIds = rec.clips.filter((clip) => !clip.uploaded).map((clip) => clip.id);
  const doneCount = states.filter((s) => s === "done").length;
  const activeIndex = states.indexOf("active");
  const failedClip = rec.clips.find((_, i) => states[i] === "failed");
  const allDone = doneCount === total;
  const waitingBytes = rec.clips.reduce(
    (sum, clip) =>
      clip.uploaded ? sum : sum + (clip.byteSize || 0) + (clip.audioBytes || 0),
    0
  );
  const firstPlayable = rec.clips.findIndex((clip) => !clip.uploaded);
  const info = rec.info || {};
  const title =
    info.rubberLabel ||
    (rec.table != null
      ? t("cmPhoneVideosTable", "Table {{n}}", { n: rec.table })
      : t("cmPhoneVideosRecording", "Recording"));
  const where = [
    info.rubberLabel && rec.table != null
      ? t("cmPhoneVideosTable", "Table {{n}}", { n: rec.table })
      : null,
    angleLabel(rec.angle, t),
    when,
  ]
    .filter(Boolean)
    .join(" · ");
  const format = [
    info.resolution ? `${info.resolution}p` : null,
    info.fps ? `${info.fps} fps` : null,
    t("cmPhoneVideosMinutes", "{{n}} min", { n: minutesOf(rec.durationMs) }),
    !allDone ? formatBytes(waitingBytes) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  let status = null;
  if (allDone) {
    status = (
      <p className="cpv-rec__status cpv-rec__status--done">
        <Check size={14} aria-hidden />
        {t("cmPhoneVideosUploaded", "Uploaded")}
      </p>
    );
  } else if (activeIndex >= 0) {
    status = (
      <p className="cpv-rec__status">
        {uploads.sent
          ? t("cmPhoneVideosSaving", "Saving part {{n}} of {{total}}…", {
              n: activeIndex + 1,
              total,
            })
          : t("cmPhoneVideosUploadingPart", "Uploading part {{n}} of {{total}} · {{pct}}%", {
              n: activeIndex + 1,
              total,
              pct: uploads.pct || 0,
            })}
      </p>
    );
  } else if (failedClip) {
    status = (
      <p className="cpv-rec__status cpv-rec__status--error">
        <AlertTriangle size={14} aria-hidden />
        {clipUploadErrorText(uploads.errors[failedClip.id], t)}
      </p>
    );
  }

  return (
    <li className={`cpv-rec${allDone ? " is-done" : ""}`}>
      <button
        type="button"
        className="cpv-rec__thumb"
        disabled={firstPlayable < 0}
        onClick={() => onPlay(firstPlayable)}
        aria-label={t("cmPhoneVideosPlay", "Play {{title}}", { title })}
      >
        {rec.thumb ? <img src={rec.thumb} alt="" /> : <Film size={22} aria-hidden />}
        {firstPlayable >= 0 ? (
          <span className="cpv-rec__play" aria-hidden>
            <Play size={16} />
          </span>
        ) : null}
      </button>
      <div className="cpv-rec__body">
        <p className="cpv-rec__title">{title}</p>
        <p className="cpv-rec__meta">{where}</p>
        <p className="cpv-rec__meta cpv-rec__meta--num">{format}</p>
      </div>
      <ol
        className="cpv-strip"
        aria-label={t("cmPhoneVideosStrip", "{{done}} of {{total}} parts uploaded", {
          done: doneCount,
          total,
        })}
      >
        {rec.clips.map((clip, i) => (
          <li
            key={clip.id}
            className={`cpv-strip__cell is-${states[i]}`}
            style={
              states[i] === "active"
                ? { "--cpv-fill": `${uploads.sent ? 100 : uploads.pct || 0}%` }
                : undefined
            }
          >
            {states[i] === "done" || clip.uploaded ? null : (
              <button
                type="button"
                className="cpv-strip__hit"
                onClick={() => onPlay(rec.clips.slice(0, i).filter((c) => !c.uploaded).length)}
                aria-label={t("cmPhoneVideosPlayPart", "Play part {{n}}", { n: i + 1 })}
              />
            )}
          </li>
        ))}
      </ol>
      {status}
      {!allDone ? (
        <div className="cpv-rec__actions">
          <button
            type="button"
            className="cpv-btn cpv-btn--ghost"
            disabled={uploads.busy || transport === "none"}
            onClick={() => onUpload(waitingIds)}
          >
            <CloudUpload size={15} aria-hidden />
            {failedClip
              ? t("cmPhoneVideosRetry", "Try again")
              : t("cmPhoneVideosUpload", "Upload")}
          </button>
          <button
            type="button"
            className="cpv-btn cpv-btn--ghost cpv-btn--danger"
            disabled={activeIndex >= 0}
            onClick={onDelete}
          >
            <Trash2 size={15} aria-hidden />
            {t("cmPhoneVideosDelete", "Delete")}
          </button>
        </div>
      ) : null}
    </li>
  );
}

function PhoneClipPlayer({ group, startIndex, t, onClose }) {
  const [playable] = useState(() =>
    (group?.clips || []).filter((clip) => !clip.uploaded)
  );
  const [index, setIndex] = useState(() =>
    Math.max(0, Math.min(Number(startIndex) || 0, playable.length - 1))
  );
  const [src, setSrc] = useState({ video: null, audio: null, missing: false });
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const closeRef = useRef(null);
  const clip = playable[index] || null;
  const clipId = clip?.id || null;

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const urls = [];
    setSrc({ video: null, audio: null, missing: false });
    if (!clipId) return undefined;
    readLocalMatchClip(clipId)
      .then((row) => {
        if (cancelled) return;
        if (!row?.blob) {
          setSrc({ video: null, audio: null, missing: true });
          return;
        }
        const video = URL.createObjectURL(row.blob);
        urls.push(video);
        let audio = null;
        if (row.audioBlob) {
          audio = URL.createObjectURL(row.audioBlob);
          urls.push(audio);
        }
        setSrc({ video, audio, missing: false });
      })
      .catch(() => {
        if (!cancelled) setSrc({ video: null, audio: null, missing: true });
      });
    return () => {
      cancelled = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [clipId]);

  const syncAudio = (play) => {
    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video || !audio) return;
    if (Math.abs(audio.currentTime - video.currentTime) > 0.25) {
      audio.currentTime = video.currentTime;
    }
    if (play === true) void audio.play().catch(() => {});
    if (play === false) audio.pause();
  };

  const info = group?.info || {};
  const title =
    info.rubberLabel ||
    (group?.table != null
      ? t("cmPhoneVideosTable", "Table {{n}}", { n: group.table })
      : t("cmPhoneVideosRecording", "Recording"));
  const hasPrev = index > 0;
  const hasNext = index < playable.length - 1;

  return (
    <div className="cpv-player" role="dialog" aria-modal="true" aria-label={title}>
      <div className="cpv-player__bar">
        <div className="cpv-player__text">
          <p className="cpv-player__title">{title}</p>
          <p className="cpv-player__meta">
            {t("cmPhoneVideosPartOf", "Part {{n}} of {{total}}", {
              n: index + 1,
              total: playable.length,
            })}
          </p>
        </div>
        <button
          ref={closeRef}
          type="button"
          className="cpv-icon-btn"
          onClick={onClose}
          aria-label={t("close", "Close")}
        >
          <X size={20} aria-hidden />
        </button>
      </div>
      <div className="cpv-player__stage">
        {src.video ? (
          <video
            key={src.video}
            ref={videoRef}
            src={src.video}
            controls
            autoPlay
            playsInline
            onPlay={() => syncAudio(true)}
            onPause={() => syncAudio(false)}
            onSeeked={() => syncAudio(null)}
            onEnded={() => {
              audioRef.current?.pause();
              if (hasNext) setIndex((i) => i + 1);
            }}
          />
        ) : (
          <p className="cpv-player__msg">
            {src.missing
              ? t(
                  "cmPhoneVideosPartGone",
                  "This part is no longer on this phone. It may have just been uploaded."
                )
              : t("loading", "Loading…")}
          </p>
        )}
        {src.audio ? <audio key={src.audio} ref={audioRef} src={src.audio} preload="auto" /> : null}
      </div>
      <div className="cpv-player__nav">
        <button
          type="button"
          className="cpv-btn cpv-btn--ghost"
          disabled={!hasPrev}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
        >
          <ChevronLeft size={16} aria-hidden />
          {t("cmPhoneVideosPrev", "Previous")}
        </button>
        <button
          type="button"
          className="cpv-btn cpv-btn--ghost"
          disabled={!hasNext}
          onClick={() => setIndex((i) => Math.min(playable.length - 1, i + 1))}
        >
          {t("cmPhoneVideosNext", "Next")}
          <ChevronRight size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}
